<template>

  <v-container fluid class="invite-shipper-member-form">

    <!-- Title and close action -->
    <v-row class="mt-1">
      <v-col cols="12" xs="12" sm="9" md="9" lg="9">
        <div style="font-size:1.5rem;">
          Invite member to {{ activeOrg.name }}
        </div>
      </v-col>
      <v-col cols="12" xs="12" sm="3" md="3" lg="3" align="right">
        <a class="pr-4" style="font-size:16px;" @click="close" v-if="!isProcessing">Close</a>
      </v-col>
    </v-row>

    <!-- (data loading spinner) -->
    <v-container v-if="isProcessing" class="mt-10 d-flex flex-column align-center justify-center">
      <v-progress-circular indeterminate :size="40" style="margin-bottom:15px" color="primary" />
      <div style="font-size:1.14rem;">Inviting member</div>
    </v-container>

    <!------------------------>
    <!-- Invite Member Form -->
    <!------------------------>
    <v-container fluid class="mt-8 page-content" v-else>
      <!-- email -->
      <v-row no-gutters align="center">
        <v-col cols="12" xs="12" sm="2" md="2" lg="2">
          <span class="attr-label">Email:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="8" md="7" lg="7">
          <v-text-field v-model.trim="memberEmail" />
        </v-col>
      </v-row>

      <!-- roles -->
      <v-row align="center">
        <v-col cols="12">
          <span class="attr-label">Roles to grant:</span>
        </v-col>
      </v-row>
      <v-row align="center" v-for="role in availableMemberRoles" :key="`role-${role.code}`">
        <v-col cols="12" xs="12" sm="6" md="4" lg="3" class="pl-6">
          <v-checkbox
              hide-details
              class="mt-0 mb-3"
              :label="role.label"
              :disabled="role.code === 'dispatcher' && isAdminSelected"
              :value="role.code"
              v-model="memberRoles"
              @change="validateRoleSelections">
            <template v-slot:label>
              <div class="role-label">{{ role.label }}</div>
            </template>
          </v-checkbox>
        </v-col>
        <v-col cols="12" xs="12" sm="6" md="8" lg="9">
          <div class="role-description">{{ role.description }}</div>
        </v-col>
      </v-row>
    </v-container>

    <!------------------------------>
    <!-- Action and Message Area -->
    <!------------------------------>
    <v-container style="text-align:center;">
      <!-- success message, invite another button -->
      <template v-if="inviteSuccessMessage">
        <transition name="fade" appear>
          <v-row class="pt-6" align="center">
            <v-col>
              <div class="success-message-text">
                <v-icon color="green" class="mr-2 pb-1">mdi-check-bold</v-icon>
                <span>{{ inviteSuccessMessage }}</span>
              </div>
            </v-col>
            <v-col>
              <v-btn text color="light-blue darken-1" @click="resetForm">
                Invite Another
              </v-btn>
            </v-col>
          </v-row>
        </transition>
      </template>

      <!-- error message -->
      <template v-else-if="inviteError">
        <transition name="fade" appear>
          <v-row class="pt-8" align="center">
            <v-col cols="9" class="error-message-text">
              <v-icon color="red darken-2" class="mr-2 pb-1">mdi-alert-box-outline</v-icon>
              <span>{{ inviteError }}</span>
            </v-col>
            <v-col cols="3">
              <v-btn text color="light-blue darken-1" @click="resetForm">
                Clear Form
              </v-btn>
            </v-col>
          </v-row>
        </transition>
      </template>

      <!-- invite member button -->
      <v-row align="center" v-else>
        <v-col class="mt-4">
          <v-btn
              :disabled="!isReadyToInvite || isProcessing"
              class="action-button"
              color="blue"
              @click="inviteMember">
            Invite Member
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

  </v-container>

</template>

<script>
export default {
  name: 'InviteShipperMemberForm',

  data () {
    return {
      isProcessing: false,
      inviteError: null,
      memberEmail: null,
      memberRoles: [],
      createdInvite: null,
      inviteSuccessMessage: null,
      invitedCount: 0
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    activeOrg () {
      return this.$store.getters['auth/activeOrg']
    },
    isReadyToInvite () {
      return this.memberEmail && this.memberRoles.length > 0
    },
    availableMemberRoles () {
      return [
        { label: 'Admin', code: 'admin', description: 'Manage provider connections, team members, and perform all dispatcher capabilities.' },
        { label: 'Dispatcher', code: 'dispatcher', description: 'Ship orders and create/dispatch routes.' },
        { label: 'Driver', code: 'driver', description: 'Accept and perform deliveries.' }
      ]
    },
    isAdminSelected () {
      return this.$root.utils.includes(this.memberRoles, 'admin')
    }
  },

  beforeDestroy () {
    this.resetForm()
  },

  methods: {
    // Emit close action
    close () {
      this.$emit('close', { invited: this.invitedCount })
      this.resetForm()
      this.invitedCount = 0
    },

    // Reset form data
    resetForm () {
      this.inviteError = null
      this.memberEmail = null
      this.memberRoles = []
      this.createdInvite = null
      this.inviteSuccessMessage = null
    },

    // Handles appropriate selection combinations of roles
    validateRoleSelections () {
      if (this.isAdminSelected) this.memberRoles = this.memberRoles.filter(it => it !== 'dispatcher')
    },

    // Perform member invite
    async inviteMember () {
      if (!this.isReadyToInvite) return false
      this.isProcessing = true

      try {
        // Invite member
        this.createdInvite = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'org/invite',
          opts: {
            bodyData: {
              email: this.memberEmail,
              roles: this.memberRoles
            }
          }
        })

        // Increment invited count
        ++this.invitedCount

        // Show success message
        this.inviteSuccessMessage = 'Member Invited'
      } catch (err) {
        this.inviteError = err.message || err.text || err
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style>
  .invite-shipper-member-form .attr-label {
    color: #9c9c9c;
    font-size: 1.0rem;
    font-weight: 400;
  }

  .invite-shipper-member-form .role-label {
    font-weight: 600;
  }

  .invite-shipper-member-form .role-description {
    color: #9c9c9c;
    font-size: 1.0rem;
    font-style: italic;
    font-weight: 400;
  }

  .invite-shipper-member-form .action-button {
    height: 52px !important;
    width: 140px;
  }

  .invite-shipper-member-form .success-message-text {
    font-size: 1.23em;
    color: #4CAF50;
    font-weight: 400;
  }

  .invite-shipper-member-form .error-message-text {
    font-size: 1.15em;
    color: #D32F2F;
    font-weight: 400;
  }
</style>
